/**
 * `files` — Drive-backed file references on the gig ("life-saving at a
 * border"). The blob lives on Drive (uploaded via the P4 pipeline into typed
 * subfolders); this row is the index entry: category, Drive id, hash, and the
 * ADR-011 pass-pipeline artifacts (rasterized PNG ref + decoded barcode).
 */
import { z } from "zod";
import { defineCollection, field } from "../define.ts";

export const files = defineCollection({
  key: "files",
  labelKey: "collections.files",
  icon: "paperclip",
  sheetTab: "Files",
  dedupFields: ["content_sha256"],
  sort: [{ field: "name" }],
  split: "gig",
  chatHint:
    "Files attached to the gig: contracts, boarding passes, hotel confirmations, visas, photos. " +
    "The binary lives on Drive in a typed subfolder; this row is the browsable index entry. " +
    "Boarding passes attached to a travel leg auto-pin for offline (ADR-011).",
  fields: {
    name: field({
      type: z.string().min(1),
      tier: "hot",
      pii: "crew",
      labelKey: "files.name",
      sheet: { column: "Name" },
      description: "File name as shown in the browser.",
    }),
    category: field({
      type: z.string().optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "files.category",
      suggestedChips: ["Travel", "Hotel", "Contracts", "Identity", "Visas", "Financials", "Tech", "Promo", "Misc"],
      sheet: { column: "Category" },
      description: "Typed Drive subfolder: Travel, Hotel, Contracts, Identity, Visas, Financials, Tech, Promo, Misc. Identity/Visas/Financials rows are crew_self/financial scoped via holder_id.",
    }),
    drive_file_id: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "files.drive_file_id",
      sheet: { column: "Drive id" },
      description: "Drive file id (the app streams previews through /api/files/:id/raw — no Drive login).",
    }),
    mime: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "files.mime",
      sheet: { column: "MIME" },
      description: "MIME type.",
    }),
    size: field({
      type: z.number().int().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "files.size",
      sheet: { column: "Size" },
      description: "Bytes.",
    }),
    content_sha256: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "files.content_sha256",
      sheet: { column: "SHA256" },
      description: "Content hash — dedup across transports (the same PDF never creates two rows).",
    }),
    holder_id: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew_self",
      labelKey: "files.holder",
      sheet: { column: "Holder" },
      description: "For personal documents (passport/visa/boarding pass): the crew member it belongs to. Scopes the row to them + owner/editor.",
    }),
    travel_item_id: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew_self",
      labelKey: "files.travel_item",
      sheet: { column: "Travel leg" },
      description: "Travel leg this file (boarding pass) belongs to — drives ADR-011 auto-pin.",
    }),
    barcode_payload: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew_self",
      labelKey: "files.barcode",
      sheet: { column: "Barcode" },
      description: "Decoded barcode data from the pass pipeline (ADR-011) — regenerated crisp at the gate; future PKPass input.",
    }),
    added_by: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "files.added_by",
      sheet: { column: "Added by" },
      description: "Who uploaded it.",
    }),
    notes: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "files.notes",
      sheet: { column: "Notes" },
      description: "Anything about this file.",
    }),
  },
});
